import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import CCLoader from '@/Atoms/CCLoader';
import AnimatedBar from '@/Atoms/AnimatedBar';
import CCColors from '@/Utils/CCColors';
import {useGetPlayerTrainingApi} from '@/Hooks/PlayerProfileApi/useGetPlayerTrainingApi';
import {PlayerTrainingResponse} from '@/Hooks/PlayerProfileApi/types';
import {ScreenName} from '@/navigators/StackConstants';
import ProfileAsset, {ProfileAssetName} from '../../ui/ProfileAsset';
import ThunderText from '../../ui/ThunderText';

const styles = StyleSheet.create({
  columns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  leftCol: {
    flex: 3,
    minWidth: 280,
  },
  rightCol: {
    flex: 2,
    minWidth: 240,
  },
  // Spec §2 card shell: radius 20, padding 24/26, soft shadow, no border.
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 24,
    paddingHorizontal: 26,
    marginBottom: 18,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  // 18px/600 dark title, matching Overview (not the old uppercase micro-label).
  sectionLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: '#262322',
    marginBottom: 14,
  },
  // Trainers render as a 3-up grid of tinted tiles (design handoff), not
  // stacked full-width rows.
  trainerGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  trainerTile: {
    backgroundColor: '#FAF6EB',
    borderRadius: 16,
    padding: 16,
    flexGrow: 1,
    flexBasis: '30%',
    minWidth: 150,
  },
  trainerSwatch: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  trainerName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
    marginBottom: 4,
  },
  trainerLevelRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
    marginBottom: 8,
  },
  trainerAcc: {
    fontSize: 11,
    color: '#888280',
  },
  trainerTileMeta: {
    fontSize: 11,
    color: '#888280',
    marginTop: 8,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  statCell: {
    backgroundColor: '#FAF6EB',
    borderRadius: 14,
    padding: 12,
    minWidth: '46%',
    flexGrow: 1,
  },
  // No fontWeight -- Thunder is single-weight; 700 synthesizes faux-bold.
  statCellValue: {
    fontSize: 20,
    color: '#4D3F37',
  },
  statCellLabel: {
    fontSize: 11,
    color: '#888280',
    marginTop: 2,
  },
  mistakeColumns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
  },
  mistakeCol: {
    flex: 1,
    minWidth: 200,
  },
  mistakeColLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: '#888280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  // Inline row: name (fixed) · cream-track bar (flex) · value (fixed right),
  // matching the design handoff.
  mistakeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingVertical: 7,
  },
  mistakeName: {
    fontSize: 12,
    color: '#4D3F37',
  },
  mistakeBarTrack: {
    flex: 1,
    height: 9,
    borderRadius: 99,
    backgroundColor: '#F5EDD3',
    overflow: 'hidden',
  },
  mistakeBarFill: {
    height: '100%',
    borderRadius: 99,
  },
  mistakeValue: {
    fontSize: 11,
    color: '#888280',
    textAlign: 'right',
  },
  emptyText: {
    fontSize: 12,
    color: '#888280',
  },
});

// The 1/2/3-move calculation trainers are three rows of one concept; the
// design shows a single "Calculation Trainer" tile. The backend also merges
// them, but does it here too so it works before that backend deploys (and it's
// idempotent once it does -- the single merged row re-collapses to itself).
type Trainer = PlayerTrainingResponse['trainers'][number];
const CALC_TYPES = [1, 2, 3];

// Per-trainer icon, per the design handoff (Intuition→game_analytics,
// Calculation→assessment, Positions/Square→tournament).
const TRAINER_ICON: Record<number, ProfileAssetName> = {
  1: 'assessment',
  2: 'assessment',
  3: 'assessment',
  4: 'tournament',
  5: 'gameAnalytics',
  6: 'fourSquares',
};

// Error-rate severity color for the piece bars (lower rate = greener) --
// thresholds tuned to the design's piece colors.
const severityColor = (pct: number) => {
  if (pct <= 10) {
    return '#3DAB9E';
  }
  if (pct <= 19) {
    return '#FACF47';
  }
  if (pct <= 29) {
    return '#E17846';
  }
  return '#CD2C39';
};

// Phase/type bars are colored categorically (the design assigns a fixed color
// per area, not a severity gradient).
const PHASE_COLOR: Record<string, string> = {
  Opening: '#3DAB9E',
  Middlegame: '#FACF47',
  Endgame: '#CD2C39',
  Tactical: '#E17846',
  Positional: '#67B3E1',
};

const combineTrainers = (trainers: Trainer[]): Trainer[] => {
  const calc = trainers.filter(t => CALC_TYPES.includes(t.trainer_type));
  const out: Trainer[] = [];
  let inserted = false;
  for (const t of trainers) {
    if (CALC_TYPES.includes(t.trainer_type)) {
      if (!inserted) {
        const attempts = calc.reduce((s, r) => s + (r.attempt_count || 0), 0);
        const acc =
          attempts > 0
            ? calc.reduce(
                (s, r) => s + r.average_accuracy * (r.attempt_count || 0),
                0,
              ) / attempts
            : calc.reduce((s, r) => s + r.average_accuracy, 0) /
              (calc.length || 1);
        const top = calc.reduce((a, b) => ((b.level || 0) > (a.level || 0) ? b : a));
        out.push({
          ...top,
          trainer_type: 1,
          label: 'Calculation Trainer',
          average_accuracy: Math.round(acc * 100) / 100,
          attempt_count: attempts,
          positions_count: calc.reduce((s, r) => s + (r.positions_count || 0), 0),
        });
        inserted = true;
      }
      continue;
    }
    out.push(t);
  }
  return out;
};

// The design always surfaces these three trainers; show a "not started" tile
// for any the player hasn't trained yet (rather than hiding it), then append
// any other real trainers (e.g. Visualization) after.
const CANONICAL = [
  {trainer_type: 1, label: 'Calculation Trainer'},
  {trainer_type: 5, label: 'Intuition Trainer'},
  {trainer_type: 4, label: 'Square Trainer'},
];

const ensureCanonical = (list: Trainer[]): Trainer[] => {
  const byType = new Map(list.map(t => [t.trainer_type, t]));
  const result: Trainer[] = CANONICAL.map(
    c =>
      byType.get(c.trainer_type) ?? {
        trainer_type: c.trainer_type,
        label: c.label,
        level: 0,
        average_accuracy: 0,
        attempt_count: 0,
        next_threshold: 0,
        positions_count: 0,
      },
  );
  for (const t of list) {
    if (!CANONICAL.some(c => c.trainer_type === t.trainer_type)) {
      result.push(t);
    }
  }
  return result;
};

type Props = {
  username?: string;
};

// Owner-only tab (per the plan) -- the caller only mounts this when
// `is_owner === true`; the backend also enforces this server-side (403 for a
// non-owner, regardless of what the frontend renders).
export default function TrainingTab({username}: Props) {
  const navigation = useNavigation<any>();
  const {data, isLoading, isError} = useGetPlayerTrainingApi(username);

  if (isLoading) {
    return (
      <Box style={styles.card}>
        <CCLoader loading={true} isComponentLoader />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box style={styles.card}>
        <CCText style={styles.emptyText}>Couldn't load training stats.</CCText>
      </Box>
    );
  }

  const trainers = ensureCanonical(combineTrainers(data.trainers));

  // Tolerate a not-yet-deployed backend that still returns the old flat
  // by_piece/by_phase maps instead of the new row arrays.
  const pieceRows = Array.isArray(data.puzzle_mistakes?.by_piece)
    ? data.puzzle_mistakes!.by_piece
    : [];
  const phaseRows = Array.isArray(data.puzzle_mistakes?.by_phase)
    ? data.puzzle_mistakes!.by_phase
    : [];

  // Tolerate a not-yet-deployed backend that predates puzzle_scorecard.
  const scorecard = data.puzzle_scorecard ?? {
    solved_this_month: 0,
    first_try: 0,
    solve_rate: null,
  };

  return (
    <>
      <View style={styles.columns}>
        <View style={styles.leftCol}>
          <Box style={styles.card}>
            <CCText style={styles.sectionLabel}>Trainers</CCText>
            {trainers.length ? (
              <View style={styles.trainerGrid}>
                {trainers.map(t => {
                  const started = t.attempt_count > 0;
                  return (
                    <Pressable
                      key={t.trainer_type}
                      testID={`training-tile-${t.trainer_type}`}
                      onPress={() =>
                        navigation.navigate(ScreenName.Visualisation, {
                          selected: t.label,
                        })
                      }
                      style={styles.trainerTile}>
                      <View style={styles.trainerSwatch}>
                        <ProfileAsset
                          name={TRAINER_ICON[t.trainer_type] ?? 'gameAnalytics'}
                          size={22}
                        />
                      </View>
                      <CCText style={styles.trainerName} numberOfLines={1}>
                        {t.label}
                      </CCText>
                      <View style={styles.trainerLevelRow}>
                        <ThunderText size={26} lineHeight={26}>
                          L{t.level}
                        </ThunderText>
                        {started ? (
                          <CCText style={styles.trainerAcc}>
                            {Math.round(t.average_accuracy)}% acc
                          </CCText>
                        ) : null}
                      </View>
                      <AnimatedBar
                        percent={t.average_accuracy}
                        color={CCColors.BlueAqua}
                      />
                      <CCText style={styles.trainerTileMeta} numberOfLines={1}>
                        {started
                          ? `${t.attempt_count} attempts · next at ${t.next_threshold}%`
                          : 'Not started'}
                      </CCText>
                    </Pressable>
                  );
                })}
              </View>
            ) : (
              <CCText style={styles.emptyText}>No trainer activity yet.</CCText>
            )}
          </Box>
        </View>

        <View style={styles.rightCol}>
          {/* flex:1 so the card fills the row, matching the Trainers card height. */}
          <Box style={[styles.card, {flex: 1}]}>
            <CCText style={styles.sectionLabel}>Puzzles from your games</CCText>
            <View style={styles.statsGrid}>
              <View style={styles.statCell}>
                <CCText
                  fontWeight="thunder"
                  style={[styles.statCellValue, {color: '#262322'}]}>
                  {scorecard.solved_this_month}
                </CCText>
                <CCText style={styles.statCellLabel}>Solved this month</CCText>
              </View>
              <View style={styles.statCell}>
                <CCText
                  fontWeight="thunder"
                  style={[styles.statCellValue, {color: '#3DAB9E'}]}>
                  {scorecard.first_try}
                </CCText>
                <CCText style={styles.statCellLabel}>First try</CCText>
              </View>
              <View style={styles.statCell}>
                <CCText
                  fontWeight="thunder"
                  style={[styles.statCellValue, {color: '#262322'}]}>
                  {scorecard.solve_rate != null
                    ? `${Math.round(scorecard.solve_rate * 100)}%`
                    : '—'}
                </CCText>
                <CCText style={styles.statCellLabel}>Solve rate</CCText>
              </View>
              <View style={styles.statCell}>
                <CCText
                  fontWeight="thunder"
                  style={[styles.statCellValue, {color: '#E17846'}]}>
                  {data.puzzle_mistakes?.misses ?? '—'}
                </CCText>
                <CCText style={styles.statCellLabel}>Misses</CCText>
              </View>
            </View>
          </Box>
        </View>
      </View>

      {pieceRows.length || phaseRows.length ? (
        <View style={styles.columns}>
          <View style={styles.leftCol}>
            <Box style={styles.card}>
              <CCText style={styles.sectionLabel}>
                Where mistakes come from
              </CCText>
              <View style={styles.mistakeColumns}>
            <View style={styles.mistakeCol}>
              <CCText style={styles.mistakeColLabel}>By piece</CCText>
              {pieceRows.map(r => (
                <View key={r.name} style={styles.mistakeRow}>
                  <CCText style={[styles.mistakeName, {width: 58}]}>
                    {r.name}
                  </CCText>
                  <View style={styles.mistakeBarTrack}>
                    <View
                      style={[
                        styles.mistakeBarFill,
                        {
                          width: `${Math.min(r.pct, 100)}%`,
                          backgroundColor: severityColor(r.pct),
                        },
                      ]}
                    />
                  </View>
                  <CCText style={[styles.mistakeValue, {width: 56}]}>
                    {r.errors}/{r.ops}
                  </CCText>
                </View>
              ))}
            </View>
            <View style={styles.mistakeCol}>
              <CCText style={styles.mistakeColLabel}>By phase & type</CCText>
              {phaseRows.map(r => (
                <View key={r.name} style={styles.mistakeRow}>
                  <CCText style={[styles.mistakeName, {width: 82}]}>
                    {r.name}
                  </CCText>
                  <View style={styles.mistakeBarTrack}>
                    <View
                      style={[
                        styles.mistakeBarFill,
                        {
                          width: `${Math.min(r.pct, 100)}%`,
                          backgroundColor:
                            PHASE_COLOR[r.name] ?? severityColor(r.pct),
                        },
                      ]}
                    />
                  </View>
                  <CCText style={[styles.mistakeValue, {width: 34}]}>
                    {r.errors}
                  </CCText>
                </View>
              ))}
              </View>
              </View>
            </Box>
          </View>
          <View style={styles.rightCol} />
        </View>
      ) : null}
    </>
  );
}
